import { useEffect, useRef, useState } from 'react';
import { getApiErrorMessage } from '@/api/axios';
import { createInvoiceStream, getInvoice } from '@/api/invoices.api';
import {
  InvoiceStatus,
  InvoiceStreamEventType,
  type Invoice,
  type InvoiceStreamEventData,
  type InvoiceStreamEventType as InvoiceStreamEventTypeValue,
} from '@/api/types';

type UseInvoiceOptions = {
  enableStream?: boolean;
  onStreamEvent?: (
    type: InvoiceStreamEventTypeValue,
    payload: InvoiceStreamEventData,
  ) => void;
};

type LoadInvoiceOptions = {
  showLoading?: boolean;
};

const invoiceStreamEventTypes = Object.values(InvoiceStreamEventType);
const INVOICE_REFRESH_INTERVAL_MS = 10_000;

function parseEventTimestamp(value?: string): number | null {
  if (!value) {
    return null;
  }

  const timestamp = Date.parse(value);

  return Number.isNaN(timestamp) ? null : timestamp;
}

export function useInvoice(
  invoiceId?: string,
  { enableStream = false, onStreamEvent }: UseInvoiceOptions = {},
) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const onStreamEventRef = useRef(onStreamEvent);
  const invoiceRef = useRef<Invoice | null>(null);
  const latestStreamTimestampRef = useRef<number | null>(null);
  const latestRequestIdRef = useRef(0);
  const loadingRequestIdRef = useRef<number | null>(null);
  const refreshInvoiceRef = useRef<
    ((options?: LoadInvoiceOptions) => Promise<void>) | null
  >(null);

  useEffect(() => {
    onStreamEventRef.current = onStreamEvent;
  }, [onStreamEvent]);

  useEffect(() => {
    invoiceRef.current = invoice;
  }, [invoice]);

  useEffect(() => {
    let isCancelled = false;

    setError(null);
    latestStreamTimestampRef.current = null;

    if (!invoiceId) {
      setInvoice(null);
      setError('Invoice ID missing');
      setLoading(false);
      return;
    }

    setInvoice(null);

    const loadInvoice = async (
      { showLoading = false }: LoadInvoiceOptions = {},
    ) => {
      const requestId = ++latestRequestIdRef.current;
      const requestedAt = Date.now();

      if (showLoading) {
        loadingRequestIdRef.current = requestId;
        setLoading(true);
      }

      try {
        const res = await getInvoice(invoiceId);

        if (isCancelled || requestId !== latestRequestIdRef.current) {
          return;
        }

        if (
          latestStreamTimestampRef.current !== null &&
          latestStreamTimestampRef.current > requestedAt
        ) {
          return;
        }

        setInvoice(res.data);
        setError(null);
      } catch (error) {
        if (isCancelled || requestId !== latestRequestIdRef.current) {
          return;
        }

        if (
          latestStreamTimestampRef.current !== null &&
          latestStreamTimestampRef.current > requestedAt
        ) {
          return;
        }

        if (showLoading || !invoiceRef.current) {
          setInvoice(null);
          setError(getApiErrorMessage(error, 'Invoice not found or expired'));
        }
      } finally {
        if (!isCancelled && loadingRequestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    };

    refreshInvoiceRef.current = loadInvoice;
    void loadInvoice({ showLoading: true });

    if (!enableStream) {
      return () => {
        isCancelled = true;

        if (refreshInvoiceRef.current === loadInvoice) {
          refreshInvoiceRef.current = null;
        }
      };
    }

    const refreshIfNeeded = () => {
      if (
        invoiceRef.current?.status === InvoiceStatus.PAID ||
        invoiceRef.current?.status === InvoiceStatus.CANCELLED
      ) {
        return;
      }

      void loadInvoice();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfNeeded();
      }
    };

    const intervalId = window.setInterval(
      refreshIfNeeded,
      INVOICE_REFRESH_INTERVAL_MS,
    );

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isCancelled = true;

      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (refreshInvoiceRef.current === loadInvoice) {
        refreshInvoiceRef.current = null;
      }
    };
  }, [enableStream, invoiceId]);

  useEffect(() => {
    if (!invoiceId || !enableStream) {
      return;
    }

    // Keep a single SSE subscription per invoice id. The effect event already
    // gives access to the latest callback without forcing re-subscription.
    const eventSource = createInvoiceStream(invoiceId);
    const listeners = invoiceStreamEventTypes.map((type) => {
      const listener = (event: Event) => {
        try {
          const payload = JSON.parse(
            (event as MessageEvent<string>).data,
          ) as InvoiceStreamEventData;
          const eventTimestamp = parseEventTimestamp(payload.updatedAt);

          if (
            eventTimestamp !== null &&
            latestStreamTimestampRef.current !== null &&
            eventTimestamp < latestStreamTimestampRef.current
          ) {
            return;
          }

          if (eventTimestamp !== null) {
            latestStreamTimestampRef.current = eventTimestamp;
          } else {
            latestStreamTimestampRef.current = Date.now();
          }

          setInvoice(payload.invoice);
          setError(null);
          setLoading(false);
          onStreamEventRef.current?.(type, payload);
        } catch {
          // Ignore malformed SSE payloads and keep the current UI state.
        }
      };

      eventSource.addEventListener(type, listener);

      return { type, listener };
    });

    return () => {
      for (const { type, listener } of listeners) {
        eventSource.removeEventListener(type, listener);
      }

      eventSource.close();
    };
  }, [enableStream, invoiceId]);

  return { invoice, loading, error };
}
